import type { ReactNode } from 'react';
import { MIN_RELIABLE_N, type ChartExplanation } from '@dma/shared';
import { QUADRANT_META, sourceIsImported, sourceLabel, statusMeta } from './format.ts';
import { InfoTooltip } from './InfoTooltip.tsx';
import { PRODUCT_NAME } from '../brand.ts';
import type { Quadrant } from './types.ts';

// Wider shell than the survey (dashboards need room). Header shows the signed-in
// admin + logout when authenticated.
export function AdminShell({
  children,
  admin,
  onLogout,
}: {
  children: ReactNode;
  admin?: { email: string; name: string } | undefined;
  onLogout?: (() => void) | undefined;
}) {
  return (
    <div className="min-h-full flex flex-col">
      <header className="w-full border-b border-black/5 bg-white/60 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <div className="brand-gradient h-6 w-6 rounded-md shadow-sm" aria-hidden />
          <span className="text-sm font-semibold tracking-tight text-ink">
            {PRODUCT_NAME} <span className="text-ink-soft">· Admin</span>
          </span>
          {admin && (
            <div className="ml-auto flex items-center gap-4">
              <span className="text-sm text-ink-soft">{admin.email}</span>
              {onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft transition hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
                >
                  Sign out
                </button>
              )}
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">{children}</main>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20 text-ink-soft" role="status">
      <div
        className="h-6 w-6 animate-spin rounded-full border-2 border-black/10"
        style={{ borderTopColor: 'var(--accent)' }}
      />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function Card({
  title,
  description,
  info,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  /** When set, renders an accessible info icon next to the title explaining the calculation. */
  info?: ChartExplanation;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-xl border border-black/5 bg-white p-5 shadow-sm ${className ?? ''}`}>
      {(title || actions) && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            {title && (
              <h2 className="flex items-center gap-1.5 text-base font-semibold text-ink">
                {title}
                {info && <InfoTooltip help={info} />}
              </h2>
            )}
            {description && <p className="mt-1 text-sm text-ink-soft">{description}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function PrimaryButton({
  children,
  type = 'button',
  onClick,
  disabled,
}: {
  children: ReactNode;
  type?: 'button' | 'submit';
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="brand-gradient inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-red-600 ring-1 ring-red-200 transition hover:bg-red-50 active:scale-[0.98]"
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft transition hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
    >
      {children}
    </button>
  );
}

const fieldClass =
  'w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-base text-ink outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]';

export function TextField({
  label,
  id,
  type = 'text',
  value,
  onChange,
  autoComplete,
  inputMode,
  maxLength,
  pattern,
  required,
}: {
  label: string;
  id: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  inputMode?: 'text' | 'numeric';
  maxLength?: number;
  pattern?: string;
  required?: boolean;
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
      {label}
      <input
        id={id}
        type={type}
        className={fieldClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        pattern={pattern}
        required={required}
      />
    </label>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function Select({
  label,
  id,
  value,
  onChange,
  options,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
      {label}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Chip({ label, chipClass }: { label: string; chipClass: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${chipClass}`}
    >
      {label}
    </span>
  );
}

export function StatusChip({ status }: { status: string }) {
  const { label, chipClass } = statusMeta(status);
  return <Chip label={label} chipClass={chipClass} />;
}

export function SourceTag({ source }: { source: string }) {
  const imported = sourceIsImported(source);
  const chipClass = imported
    ? 'bg-amber-50 text-amber-800 ring-amber-200'
    : 'bg-slate-50 text-slate-600 ring-slate-200';
  return <Chip label={sourceLabel(source)} chipClass={chipClass} />;
}

/** Set once at creation and frozen thereafter — see services/onboarding.ts. */
export function AssessmentTypeChip({ assessmentType }: { assessmentType: string }) {
  const isLightweight = assessmentType === 'lightweight';
  const chipClass = isLightweight
    ? 'bg-violet-50 text-violet-700 ring-violet-200'
    : 'bg-sky-50 text-sky-700 ring-sky-200';
  return <Chip label={isLightweight ? 'Lightweight' : 'Full Dimension'} chipClass={chipClass} />;
}

/**
 * Marks a mean built on too few respondents (n < MIN_RELIABLE_N) as "indicative
 * only". Text + tooltip, not colour alone (WCAG). The number itself is unchanged —
 * skipped answers are excluded from the math, never imputed; this only flags low n.
 */
export function LowNBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200"
      title={`Fewer than ${MIN_RELIABLE_N} respondents — interpret this mean with caution.`}
      aria-label={`Low sample: fewer than ${MIN_RELIABLE_N} respondents, interpret with caution`}
    >
      low n
    </span>
  );
}

export function QuadrantChip({ quadrant }: { quadrant: Quadrant | null }) {
  if (!quadrant) return <span className="text-ink-soft">—</span>;
  const meta = QUADRANT_META[quadrant];
  return <Chip label={meta.label} chipClass={meta.chipClass} />;
}
